<template>
    <div class="jdoc-search">
        <!-- 移动端：工程筛选开关（桌面端隐藏，左侧栏常驻） -->
        <button type="button" class="jdoc-search__toggle" @click="filterOpen = !filterOpen">
            <el-icon><Operation /></el-icon>
            <span>工程筛选{{ selectProjectList.length > 0 ? '（' + selectProjectList.length + '）' : '' }}</span>
            <el-icon>
                <ArrowDown v-if="!filterOpen" />
                <ArrowUp v-else />
            </el-icon>
        </button>

        <!-- 左侧：工程筛选 -->
        <aside class="jdoc-search__side jdoc-panel" :class="{ 'is-open': filterOpen }">
            <div class="jdoc-panel__head">
                <el-icon><OfficeBuilding /></el-icon>
                <span>工程筛选</span>
                <el-tag v-if="selectProjectList.length > 0" size="small" type="primary" effect="plain">{{ selectProjectList.length }}</el-tag>
                <el-link v-if="selectProjectList.length > 0" class="jdoc-search__clear" type="primary" :underline="false" @click="clearProjects">清空</el-link>
            </div>
            <div ref="projectTree" class="jdoc-panel__body jdoc-tree">
                <el-tree v-if="projectList.length > 0" :data="projectList" node-key="id" default-expand-all :expand-on-click-node="false" @node-click="selectProject">
                    <template #default="{ data }">
                        <span class="jdoc-tree__node" :class="{ 'is-active': selectProjectList.indexOf(data.name) > -1 }">{{ data.name }}</span>
                    </template>
                </el-tree>
                <el-empty v-else description="暂无可访问工程" :image-size="60" />
            </div>
            <div class="jdoc-search__sidetip">默认不选择，查询全部有权限的工程</div>
        </aside>

        <!-- 右侧：搜索条件与结果 -->
        <section class="jdoc-search__main">
            <div class="jdoc-panel jdoc-search__bar">
                <div class="jdoc-search__fields">
                    <el-input ref="searchName" v-model="searchName" placeholder="类名、方法名" class="input-with-select" @keydown="searchEnter" clearable>
                        <template #prefix>
                            <el-icon><Search /></el-icon>
                        </template>
                    </el-input>
                    <el-input ref="searchText" v-model="searchText" placeholder="文本内容，多个词用空格分隔" class="input-with-select" @keydown="searchEnter" clearable>
                        <template #prefix>
                            <el-icon><Document /></el-icon>
                        </template>
                    </el-input>
                </div>
                <div class="jdoc-search__opts">
                    <el-tooltip placement="bottom" effect="light">
                        <template #content>
                            <p>结构：#场景、#限制、#关键字 至少包括一种的描述</p>
                            <p>全部：查询所有数据（包括不带描述的数据）</p>
                        </template>
                        <el-switch ref="searchIsStruct" v-model="searchIsStruct" width="50" inline-prompt active-text="结构" inactive-text="全部" />
                    </el-tooltip>
                    <el-tooltip placement="bottom" effect="light">
                        <template #content>
                            <p>正常：查询不包括废弃的数据</p>
                            <p>全部：查询所有数据（不区分是否废弃）</p>
                        </template>
                        <el-switch ref="searchIsDeprecated" v-model="searchIsDeprecated" width="50" inline-prompt active-text="全部" inactive-text="正常" />
                    </el-tooltip>
                </div>
                <div class="jdoc-search__submit">
                    <el-button ref="searchButton" type="primary" :loading="loading" @click="search()">
                        <el-icon v-if="!loading"><Search /></el-icon>
                        <span>搜索</span>
                    </el-button>
                    <el-button type="danger" link size="small" @click="showHelpTour()">帮助</el-button>
                </div>
            </div>

            <div ref="resultBox" class="jdoc-search__result" v-loading="loading">
                <!-- =================================================================================================================== -->
                <!-- 帮助引导模式：展示演示卡片，作为 el-tour 各步骤的讲解锚点 -->
                <div v-if="helpTour" class="jdoc-card">
                    <div class="jdoc-card__head">
                        <span id="metaType" class="jdoc-card__kind is-class">类</span>
                        <div class="jdoc-card__title">
                            <el-tag id="metaIsDeprecated" type="danger" size="small" effect="light">废弃</el-tag>
                            <el-tag id="metaQualifier" type="success" size="small" effect="light">public static</el-tag>
                            <span id="metaClassName" class="jdoc-card__class">className</span>
                            <span class="jdoc-card__dot">.</span>
                            <span id="metaMethodName" class="jdoc-card__name">methodName</span>
                            <el-icon id="metaNameCopy" class="jdoc-card__copy"><CopyDocument /></el-icon>
                            <el-rate id="metaHelpfulRate" class="jdoc-card__rate" v-model="CONST_FIVE" />
                        </div>
                        <span id="metaProjectName" class="jdoc-card__project">projectName</span>
                    </div>
                    <div class="jdoc-card__brief">
                        <div id="metaCommentInfo" class="jdoc-card__info">概述基本信息</div>
                        <div class="jdoc-card__acts">
                            <el-button id="metaExp" type="primary" size="small" plain round>示例</el-button>
                            <el-button id="metaLog" type="warning" size="small" plain round>日志</el-button>
                            <el-button id="metaCode" type="danger" size="small" plain round>Code</el-button>
                        </div>
                    </div>
                    <div id="metaCommentScene" class="jdoc-card__line jdoc-card__line--scene">
                        <span class="jdoc-card__emoji">🎬️</span>
                        <span class="jdoc-card__strong">场景：</span>
                        描述什么时候可以使用
                    </div>
                    <div class="jdoc-card__io">
                        <div id="metaParams" class="jdoc-card__iobox">
                            <div class="jdoc-card__iocap">传入参数</div>
                            <p class="jdoc-card__iorow">
                                <span class="jdoc-card__emoji">➡️</span>
                                <el-text type="primary">String</el-text>
                                <span class="jdoc-card__colon">:</span>
                                <el-text type="warning">单据号</el-text>
                            </p>
                            <p class="jdoc-card__iorow">
                                <span class="jdoc-card__emoji">➡️</span>
                                <el-text type="primary">Date</el-text>
                                <span class="jdoc-card__colon">:</span>
                                <el-text type="warning">记账时间</el-text>
                            </p>
                        </div>
                        <div id="metaReturn" class="jdoc-card__iobox">
                            <div class="jdoc-card__iocap">返回值 / 异常</div>
                            <p class="jdoc-card__iorow">
                                <span class="jdoc-card__emoji">↩️</span>
                                <el-text type="primary">bool</el-text>
                                <span class="jdoc-card__colon">:</span>
                                <el-text type="warning">操作成功标志</el-text>
                            </p>
                            <p class="jdoc-card__iorow">
                                <span class="jdoc-card__emoji">🐞</span>
                                <el-text type="primary">IOException</el-text>
                                <span class="jdoc-card__colon">:</span>
                                <el-text type="warning">文件流处理要注意IO异常</el-text>
                            </p>
                        </div>
                    </div>
                    <div id="metaCommentLimit" class="jdoc-card__line jdoc-card__line--limit">
                        <span class="jdoc-card__emoji">⛔️</span>
                        <span class="jdoc-card__strong">限制：</span>
                        描述不能使用的情况或需注意的事项
                    </div>
                    <!-- 关键字 -->
                    <div class="jdoc-card__keywords">
                        <el-tag id="metaKeyword" type="warning" size="small" effect="plain">单据记账</el-tag>
                        <el-tag type="warning" size="small" effect="plain">数据查询</el-tag>
                    </div>
                    <div class="jdoc-card__foot">
                        <span id="metaCreateTime">2024-01-01 09:00:00 刷新</span>
                    </div>
                </div>
                <!-- =================================================================================================================== -->

                <!-- 正常查询结果 -->
                <template v-else>
                    <div v-if="javadocItem.length > 0" class="jdoc-search__count">共 {{ javadocItem.length }} 条结果</div>
                    <java-doc-item-card v-for="(item, index) in javadocItem" :key="item.id || index" v-bind:data="item"></java-doc-item-card>
                    <div v-if="javadocItem.length == 0" class="jdoc-empty">
                        <el-icon><Search /></el-icon>
                        <div class="jdoc-empty__title">输入类名或注释内容后开始搜索</div>
                        <div class="jdoc-empty__desc">多个关键词用空格分隔，结果中的关键词会高亮显示</div>
                    </div>
                </template>
            </div>
        </section>
    </div>

    <el-tour v-model="helpTour">
        <el-tour-step :target="$refs.projectTree" title="🏢 工程列表" placement="right">
            <div>您可以在这里点击某些工程，来筛选从哪些工程查询信息</div>
            <div>默认不选择，会查询有权限的所有工程</div>
        </el-tour-step>
        <el-tour-step :target="$refs.searchName?.$el" title="📁 搜索类或方法" description="在这里可以输入类名或方法名，可以较为精准的定位到需要的信息" />
        <el-tour-step :target="$refs.searchText?.$el" title="📄 搜索注释内容" description="您可以输入文本，进行注释内容的查询，多个词用空格隔开，结果会以高亮显示" placement="bottom-end" />
        <el-tour-step :target="$refs.searchIsStruct?.$el" title="📰 只搜索结构化后的注释吗？" placement="bottom-end">
            <p>结构化的注释，指的是注释中，包含至少一种描述，如：#场景、#限制、#关键字</p>
            <p>您也可以切换到从全部注释中搜索，这样搜索范围较大</p>
        </el-tour-step>
        <el-tour-step :target="$refs.searchIsDeprecated?.$el" title="⛓️‍💥 要搜索废弃的类或方法？" placement="bottom-end">
            <p>通常我们会使用 @Deprecated 注解，来标识淘汰的类或方法</p>
            <p>这里默认搜索会排除掉淘汰的类或方法，如果需要查询他们，可以切换到搜索全部信息</p>
        </el-tour-step>
        <el-tour-step :target="$refs.searchButton?.$el" title="🔍️ 搜索" description="开始搜索吧，或者在文本框输入信息后，直接按 回车（Enter）看看" placement="bottom-end" />
        <el-tour-step target="#metaType" title="🔨 类还是方法" description="这里标识内容是类还是代码，会根据不同类型展示不同内容" placement="bottom" />
        <el-tour-step target="#metaIsDeprecated" title="🚫 淘汰的类或方法" description="通常不建议使用淘汰的代码，可以根据注释提示，换用新的方法" placement="bottom" />
        <el-tour-step target="#metaQualifier" title="🌻 修饰符" description="代码修饰符，常见的有：public、private、static 等" placement="bottom" />
        <el-tour-step target="#metaClassName" title="🔖 类名称" description="这里展示类的名称，可以通过类名称到代码中找到准确位置" placement="bottom" />
        <el-tour-step target="#metaMethodName" title="🏷️ 方法名称" description="这里是方法名称，可以通过他查找代码里更详细的内容" placement="bottom" />
        <el-tour-step target="#metaNameCopy" title="📋️ 复制" description="点击这里可以复制类名和方法名，格式如：className.methodName" placement="bottom" />
        <el-tour-step target="#metaHelpfulRate" title="⭐️ 推荐" description="这里可以查看大家对代码的推荐程度，您也可以登录进行推荐打分" placement="bottom" />
        <el-tour-step target="#metaProjectName" title="🏗️ 工程名称" description="指代码来源于哪个工程，追溯时可有依据" placement="bottom-end" />
        <el-tour-step target="#metaCommentInfo" title="🐵 基本描述" description="注释的基本描述，通常是第一行注释信息" placement="bottom" />
        <el-tour-step target="#metaExp" title="👌 说明示例" description="这里可以查看在注释中写的示例，示例可以支持 html、markdown 格式展示" placement="bottom-end" />
        <el-tour-step target="#metaLog" title="📅 修改日志" description="按照开发规范，我们需要在代码类上方增加日志信息，这里可以展示结构化的日志信息" placement="bottom-end" />
        <el-tour-step target="#metaCode" title="👾 查看源码" description="如果需要立即查看源码，可以点击这里，注意此功能需要登录使用" placement="bottom-end" />
        <el-tour-step target="#metaCommentScene" title="🎬 适用场景" description="每段代码都有其适用的场景，可以参考这里判断能不能在你的代码中复用" placement="bottom" />
        <el-tour-step target="#metaParams" title="➡️ 传入参数" description="调用方法时，可能需要传入的参数，这里包括类型和说明信息" placement="bottom" />
        <el-tour-step target="#metaReturn" title="↩️ 返回值 及 🐞 异常" placement="bottom">
            <p>↩️ 返回值：类型和数据是不是你需要的</p>
            <p>🐞 异常：您需要注意识别和应对，避免突发状况</p>
        </el-tour-step>
        <el-tour-step target="#metaCommentLimit" title="⛔️ 使用限制" description="有些方法可能存在一些限定条件，为避免影响，请关注使用限制" placement="bottom" />
        <el-tour-step target="#metaKeyword" title="🗝️ 关键字" description="同类型的代码，可以使用关键字来区分，也能更好的帮助大家来搜索" placement="bottom" />
        <el-tour-step target="#metaCreateTime" title="🕙 刷新时间" placement="bottom">
            <p>代码注释每小时都会自动更新到这里</p>
        </el-tour-step>
        <el-tour-step title="🎉 完成学习" content-style="width:800">
            <p>恭喜你，完成了基本操作步骤的学习，现在可以开始使用了~</p>
            <p>另外：</p>
            <p>如果您有更好的见解，可以在代码注释那里做修改，示例如下：</p>
            <v-md-editor v-model="this.helpTourCommentExp" mode="preview" />
        </el-tour-step>
    </el-tour>
</template>

<script>
import { ElMessage } from 'element-plus';
import JavaDocItemCard from './JavaDocItemCard';
import request from '../../../utils/request.js';
import _ from 'lodash';
export default {
    data() {
        return {
            CONST_FIVE: 5,
            helpTourCommentExp: `
\`\`\` java
/**
 * 根据主键列表查询数据
 * #场景 批量记账、批量审核等场景，可以根据前端传入id批量查询
 * #限制 不能用于联合主键的表
 * #关键字 批量查询
 *
 * @param iDataAdapter 数据连接
 * @param idList 主键列表
 * @return 平台表对象
 */
public DataTable selectByIds(IDataAdapter iDataAdapter, List idList)
\`\`\`
            `,
            helpTour: false,
            loading: false,
            searchText: '',
            searchName: '',
            searchMode: 'all',
            searchIsStruct: true,
            searchIsDeprecated: false,
            javadocItem: [],
            filterOpen: false,
            selectProjectList: [],
            projectList: [],
            viewData: {
                currentProjectId: '',
                currentVersion: '',
                currentPackageName: '',
                currentClassId: '',
                packageList: [],
                classList: [],
                methodList: []
            }
        };
    },
    components: { JavaDocItemCard },
    mounted() {
        this.getProjectList();
    },
    methods: {
        selectProject(data) {
            if (this.selectProjectList.indexOf(data.name) > -1) {
                this.selectProjectList = this.selectProjectList.filter(item => item !== data.name);
            } else {
                this.selectProjectList.push(data.name);
            }
        },
        clearProjects() {
            this.selectProjectList = [];
        },
        searchEnter(e) {
            if (e.keyCode == 13) {
                this.search();
            }
        },
        search() {
            if (this.loading) return;
            this.loading = true;
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(this.doSearch, 250);
        },
        doSearch() {
            console.log('搜索 ' + this.searchMode + ' ' + this.searchText);
            return request({
                url: '/openapi/javadoc/search',
                method: 'post',
                data: {
                    mode: this.searchMode,
                    name: this.searchName,
                    text: this.searchText,
                    projects: this.selectProjectList,
                    isStruct: this.searchIsStruct,
                    isDeprecated: this.searchIsDeprecated
                }
            })
                .then(res => {
                    if (res.code == 0) {
                        // json字符串格式化成对象
                        _(res.data).forEach(function (item) {
                            // 转换meta的字符串为对象
                            if (item.imports) item.imports = JSON.parse(item.imports);
                            if (item.params) item.params = JSON.parse(item.params);
                            if (item.throwses) item.throwses = JSON.parse(item.throwses);
                            if (item.commentLog) item.commentLog = JSON.parse(item.commentLog);
                            // 转换类信息的字符串为对象
                            if (item.javaDocClassLite && item.javaDocClassLite.commentLog) item.javaDocClassLite.commentLog = JSON.parse(item.javaDocClassLite.commentLog);
                        });

                        this.javadocItem = res.data || [];
                        // 新结果从顶部开始浏览
                        this.$nextTick(() => {
                            if (this.$refs.resultBox) this.$refs.resultBox.scrollTop = 0;
                        });

                        ElMessage({
                            message: '搜索完成',
                            type: 'success',
                            duration: 1 * 1000
                        });
                    }
                })
                .catch(() => { })
                .finally(() => {
                    this.loading = false;
                });
        },
        gotoPage(page) {
            if (page == 'preview') {
                let routeData = this.$router.resolve({
                    name: 'preview',
                    params: {}
                });
                window.open(routeData.path, '_blank');
            }
            if (page == 'javadoc') {
                let routeData = this.$router.resolve({
                    name: 'javadoc',
                    params: {}
                });
                window.open(routeData.path, '_blank');
            }
        },
        getProjectList() {
            request({
                url: '/openapi/javadoc/projectList',
                method: 'post',
                data: { p: 'n' }
            }).then(res => {
                if (res.code == 0) {
                    this.projectList = res.data || [];
                }
            });
        },
        getPackageList(projectId, version) {
            this.viewData.currentProjectId = projectId;
            this.viewData.currentVersion = version;
            request({
                url: '/openapi/javadoc/packageList',
                method: 'post',
                data: {
                    projectId: this.viewData.currentProjectId,
                    version: this.viewData.currentVersion
                }
            }).then(res => {
                if (res.code == 0) {
                    this.viewData.packageList = res.data || [];
                }
            });
        },
        showHelpTour() {
            // 引导第一步指向工程筛选，先确保它在窄屏下处于展开状态
            this.filterOpen = true;
            this.helpTour = true;
        }
    }
};
</script>

<style>
.jdoc-search {
    display: flex;
    gap: 12px;
    height: 100%;
}

/* 移动端才出现的工程筛选开关 */
.jdoc-search__toggle {
    display: none;
}

/* ---------- 左侧工程筛选 ---------- */
.jdoc-search__side {
    flex: none;
    width: 230px;
}

.jdoc-search__clear {
    margin-left: auto;
    font-size: 12px;
}

.jdoc-search__sidetip {
    flex: none;
    padding: 8px 12px;
    border-top: 1px solid var(--jdoc-line);
    font-size: 12px;
    line-height: 16px;
    color: var(--jdoc-muted);
}

/* ---------- 右侧主区域 ---------- */
.jdoc-search__main {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
    min-height: 0;
}

.jdoc-search .jdoc-search__bar {
    flex: none;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
}

.jdoc-search__fields {
    display: flex;
    flex: 1 1 320px;
    gap: 10px;
    min-width: 0;
}

.jdoc-search__opts {
    display: flex;
    flex: none;
    align-items: center;
    gap: 16px;
}

.jdoc-search__submit {
    display: flex;
    flex: none;
    align-items: center;
    gap: 10px;
}

.jdoc-search__result {
    flex: 1 1 auto;
    min-height: 0;
    padding-right: 4px;
    overflow: auto;
}

.jdoc-search__count {
    margin-bottom: 8px;
    font-size: 12px;
    color: var(--jdoc-muted);
}

/* 引导气泡内的 markdown 预览：去掉主题左右 2.5rem 内边距，避免气泡内内容过窄 */
.el-tour__content .vuepress-markdown-body {
    padding-left: 0;
    padding-right: 0;
}
/* 代码块自带左右 -1.5rem 负外边距（用于抵扣主题的 2.5rem 内边距），需一并归零，
   否则代码块两侧溢出，行号列与复制按钮会被裁切 */
.el-tour__content .vuepress-markdown-body div[class*=v-md-pre-wrapper-] {
    margin-left: 0;
    margin-right: 0;
}

/* ---------- 小尺寸设备适配 ---------- */
@media (max-width: 768px) {
    .jdoc-search {
        flex-direction: column;
        gap: 10px;
        height: auto;
    }

    .jdoc-search__toggle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        height: 38px;
        border: 1px solid var(--jdoc-line);
        border-radius: 10px;
        font-family: inherit;
        font-size: 13px;
        color: var(--jdoc-sub);
        background-color: #fff;
        cursor: pointer;
    }

    /* 折叠态：仅在点击开关后展开工程筛选 */
    .jdoc-search .jdoc-search__side {
        display: none;
    }

    .jdoc-search .jdoc-search__side.is-open {
        display: flex;
        width: auto;
    }

    .jdoc-search__side .jdoc-panel__body {
        max-height: 240px;
    }

    .jdoc-search__sidetip {
        display: none;
    }

    .jdoc-search .jdoc-search__bar {
        flex-direction: column;
        align-items: stretch;
    }

    .jdoc-search__fields {
        flex: 1 1 auto;
        flex-direction: column;
    }

    .jdoc-search__opts {
        justify-content: space-between;
    }

    .jdoc-search__submit {
        justify-content: flex-end;
    }

    /* 整页随外层滚动，结果区不再单独滚动 */
    .jdoc-search__result {
        overflow: visible;
        padding-right: 0;
    }
}
</style>
